import React, { createContext, useContext, useEffect, useState } from "react";
import { supabase, SUPABASE_ENABLED } from "../lib/supabase";
import { useLocale } from "@/context/LocaleContext";

type Profile = {
  name?: string;
  phone?: string;
  address?: string;
  city?: string;
  postal?: string;
  country?: string;
};

type User = { email: string; profile?: Profile } | null;

interface AuthContextValue {
  user: User;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    profile?: Profile,
  ) => Promise<void>;
  logout: () => void;
  resetPassword: (email: string, newPassword?: string) => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);
const STORAGE_KEY = "fusion.user";
const USERS_KEY = "fusion.users";

function readUsers(): Record<string, { password: string; profile?: Profile }> {
  try {
    const raw = localStorage.getItem(USERS_KEY);
    if (!raw) return {};
    return JSON.parse(raw);
  } catch {
    return {};
  }
}
function writeUsers(
  users: Record<string, { password: string; profile?: Profile }>,
) {
  try {
    localStorage.setItem(USERS_KEY, JSON.stringify(users));
  } catch {}
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let subscription: any = null;
    const init = async () => {
      if (SUPABASE_ENABLED) {
        // get current session
        try {
          const { data } = await supabase.auth.getSession();
          const session = data.session;
          if (mounted && session?.user?.email)
            setUser({ email: session.user.email });
        } catch {}
        // subscribe to changes
        try {
          const { data: sub } = supabase.auth.onAuthStateChange(
            (_event, session) => {
              if (session?.user?.email) setUser({ email: session.user.email });
              else setUser(null);
            },
          );
          subscription = sub?.subscription;
        } catch {}
      } else {
        try {
          const raw = localStorage.getItem(STORAGE_KEY);
          if (raw) setUser(JSON.parse(raw));
        } catch {}
      }
    };
    init().finally(() => {
      setLoading(false);
    });
    return () => {
      mounted = false;
      try {
        subscription?.unsubscribe?.();
      } catch {}
    };
  }, []);

  const { t } = useLocale();

  const login = async (email: string, password: string) => {
    const normalizedEmail = String(email || "")
      .trim()
      .toLowerCase();
    if (!normalizedEmail || !password)
      throw new Error(t("auth.error.email_required"));

    if (SUPABASE_ENABLED) {
      const res = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password,
      });
      if (res.error) throw res.error;
      const userEmail = res.data.user?.email;
      if (userEmail) {
        setUser({ email: userEmail });
      }
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ email: userEmail || normalizedEmail }),
        );
      } catch (e) {
        console.error(
          "[Auth] failed to persist session after supabase signIn:",
          e,
        );
      }
      return;
    }

    const users = readUsers();
    // If no users registered yet, reject login to encourage registration
    const stored = users[normalizedEmail];
    if (!stored) throw new Error(t("auth.error.user_not_found"));
    if (stored.password !== password)
      throw new Error(t("auth.error.wrong_password"));

    const u = { email: normalizedEmail, profile: stored.profile };
    setUser(u);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(u));
    } catch (e) {
      console.error("[Auth] failed to persist session to localStorage:", e);
    }
  };

  const register = async (
    email: string,
    password: string,
    profile?: Profile,
  ) => {
    const normalizedEmail = String(email || "")
      .trim()
      .toLowerCase();
    if (!normalizedEmail || !password)
      throw new Error("Email and password required");
    if (SUPABASE_ENABLED) {
      // Create the user in Supabase
      const res = await supabase.auth.signUp({
        email: normalizedEmail,
        password,
      });
      if (res.error) throw res.error;

      // Try to immediately sign in so the user can use the app without needing
      // to perform an additional password reset or email confirmation step.
      try {
        const loginRes = await supabase.auth.signInWithPassword({
          email: normalizedEmail,
          password,
        });
        if (loginRes.error) {
          // If sign-in after sign-up fails (for example because the project
          // requires email confirmations), we still surface the original
          // signUp success to the caller but do not create a session.
          const userEmail = res.data.user?.email || normalizedEmail;
          setUser({ email: userEmail, profile });
          try {
            localStorage.setItem(
              STORAGE_KEY,
              JSON.stringify({ email: userEmail, profile }),
            );
          } catch (e) {
            console.error(
              "[Auth] failed to persist session after supabase signUp:",
              e,
            );
          }
          return;
        }

        const userEmail =
          loginRes.data.user?.email || res.data.user?.email || normalizedEmail;
        setUser({ email: userEmail, profile });
        try {
          localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({ email: userEmail, profile }),
          );
        } catch (e) {
          console.error(
            "[Auth] failed to persist session after supabase signIn:",
            e,
          );
        }
        return;
      } catch (e) {
        // If anything unexpected happens, fallback to setting the user based on signUp
        const userEmail = res.data.user?.email || normalizedEmail;
        setUser({ email: userEmail, profile });
        try {
          localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({ email: userEmail, profile }),
          );
        } catch (err) {
          console.error(
            "[Auth] failed to persist session after supabase signUp (fallback):",
            err,
          );
        }
        return;
      }
    }

    const users = readUsers();
    const key = normalizedEmail;
    if (users[key]) throw new Error(t("auth.error.account_exists"));
    users[key] = { password, profile };
    try {
      writeUsers(users);
      const verify = readUsers();
      if (!verify[key])
        throw new Error("Failed to persist user in local storage");
    } catch (e) {
      console.error("[Auth] failed to write users to localStorage:", e);
      throw new Error(t("login.error.register_failed"));
    }
    const u = { email: normalizedEmail, profile };
    setUser(u);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(u));
    } catch (e) {
      console.error("[Auth] failed to persist session to localStorage:", e);
    }
  };

  const logout = async () => {
    if (SUPABASE_ENABLED) {
      try {
        await supabase.auth.signOut();
      } catch {}
      setUser(null);
      return;
    }
    setUser(null);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
  };

  const resetPassword = async (email: string, newPassword?: string) => {
    const normalizedEmail = String(email || "")
      .trim()
      .toLowerCase();
    if (!normalizedEmail) throw new Error(t("auth.error.email_required"));
    const np = newPassword || "123456789";
    if (SUPABASE_ENABLED) {
      try {
        // Trigger Supabase password reset email to the user. The project will
        // handle the secure password update via the emailed link.
        // Note: resetPasswordForEmail is available on the auth client in
        // Supabase JS. Use a non-typed access to avoid build-time type issues.
        const redirectTo =
          typeof window !== "undefined" ? window.location.origin : undefined;
        const { error } = await (supabase as any).auth.resetPasswordForEmail(
          normalizedEmail,
          { redirectTo },
        );
        if (error) throw error;
        return;
      } catch (e) {
        console.error("[Auth] supabase resetPassword failed:", e);
        throw new Error(t("login.error.reset_failed"));
      }
    }

    const users = readUsers();
    const key = normalizedEmail;
    const stored = users[key];
    if (!stored) {
      // create a new account with the temporary password
      users[key] = { password: np };
    } else {
      users[key] = { password: np, profile: stored.profile };
    }
    try {
      writeUsers(users);
    } catch (e) {
      console.error("[Auth] failed to write users during reset:", e);
      throw new Error(t("login.error.reset_failed"));
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, register, logout, resetPassword }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    console.warn("useAuth called outside AuthProvider, returning null context");
    return {
      user: null,
      loading: false,
      login: async () => {},
      register: async () => {},
      logout: () => {},
      resetPassword: async () => {},
    } as AuthContextValue;
  }
  return ctx;
}
